import React, { useRef, useState } from "react";
import { App } from "antd";
import {
  DeleteOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  FilePptOutlined,
  FileWordOutlined,
  FileZipOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import PageHeader from "./PageHeader";
import OfficePdfResultActions from "./OfficePdfResultActions";
import ConversionSuccessModal from "./ConversionSuccessModal";
import "../pages/PdfTools.css";

const TARGET_META = {
  Word: { label: "Word", icon: <FileWordOutlined />, color: "#2b5ce6" },
  Excel: { label: "Excel", icon: <FileExcelOutlined />, color: "#1a7a43" },
  PowerPoint: { label: "PowerPoint", icon: <FilePptOutlined />, color: "#c84b1e" },
  Images: { label: "ZIP", icon: <FileZipOutlined />, color: "#7b3fe4" },
};

const stripFileNamePrefix = (fileName = "") => {
  const separatorIndex = fileName.indexOf("_");
  return separatorIndex >= 0 ? fileName.slice(separatorIndex + 1) : fileName;
};

const PdfToOfficeConverter = ({ target, title, description }) => {
  const { message } = App.useApp();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const fileRef = useRef(null);

  const meta = TARGET_META[target];

  const pickFile = (incoming) => {
    if (uploading) {
      return;
    }

    const picked = Array.from(incoming || []).find(
      (item) => item.type === "application/pdf" || item.name.toLowerCase().endsWith(".pdf")
    );

    if (!picked) {
      message.error("Please select a PDF file.");
      return;
    }

    setFile(picked);
    setResult(null);
    setShowSuccessModal(false);
  };

  const removeFile = () => {
    if (uploading) {
      return;
    }
    setFile(null);
    setResult(null);
    setShowSuccessModal(false);
  };

  const handleConvert = async () => {
    if (!file) {
      message.error("Please select a PDF file.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("Source", "Web");
    formData.append("Target", target);

    try {
      setUploading(true);
      setResult(null);

      const token = localStorage.getItem("token") || "";
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/Pdf/reverse-pdf-file`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const data = response.data?.data;

      if (!response.data?.error && data?.downloadUrl) {
        setResult({
          downloadUrl: data.downloadUrl,
          fileName: stripFileNamePrefix(data.fileName) || file.name,
          outputType: data.outputType === "images" ? "zip" : data.outputType,
        });
        setShowSuccessModal(true);
      } else {
        message.error(response.data?.message || "Conversion failed.");
      }
    } catch (error) {
      console.error(error);
      message.error(error?.response?.data?.message || "Conversion failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <PageHeader />

      <div className="pt-root pt-module">
        <div className="pt-inner">
          <div className="pt-header">
            <div className="pt-header-icon" style={{ background: `${meta.color}12`, color: meta.color }}>
              {meta.icon}
            </div>
            <div>
              <h1 className="pt-title">{title}</h1>
              <p className="pt-subtitle">{description}</p>
            </div>
          </div>

          {!file && (
            <div
              className={`pt-dropzone ${dragOver ? "drag-over" : ""}`}
              onDragOver={(event) => {
                event.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragOver(false);
                pickFile(event.dataTransfer.files);
              }}
              onClick={() => fileRef.current?.click()}
            >
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,application/pdf"
                style={{ display: "none" }}
                onChange={(event) => {
                  pickFile(event.target.files);
                  event.target.value = "";
                }}
              />
              <FilePdfOutlined className="pt-dropzone-icon" />
              <div className="pt-dropzone-text">Click to select or drag your PDF here</div>
              <div className="pt-dropzone-hint">Only PDF files supported</div>
              <button
                type="button"
                className="pt-choose-btn"
                onClick={(event) => {
                  event.stopPropagation();
                  fileRef.current?.click();
                }}
              >
                <UploadOutlined /> Choose File
              </button>
            </div>
          )}

          {file && (
            <div className="pt-file-row">
              <FilePdfOutlined className="pt-file-icon" />
              <span className="pt-file-name">{file.name}</span>
              <span className="pt-file-size">{(file.size / 1024).toFixed(0)} KB</span>
              <button type="button" className="pt-file-remove" onClick={removeFile} disabled={uploading}>
                <DeleteOutlined />
              </button>
            </div>
          )}

          {result && (
            <OfficePdfResultActions
              downloadUrl={result.downloadUrl}
              originalFileName={result.fileName}
              outputType={result.outputType}
              fileTypeLabel={meta.label}
              fileTypeIcon={meta.icon}
              displayFileName={result.fileName}
              requiresAuth
            />
          )}

          <button
            type="button"
            className="pt-action-btn"
            onClick={handleConvert}
            disabled={!file || uploading}
          >
            {uploading ? <span className="pt-spinner" /> : meta.icon}
            {uploading ? "Converting…" : `Convert to ${meta.label}`}
          </button>
        </div>
      </div>

      <ConversionSuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        fileName={result?.fileName}
        downloadUrl={result?.downloadUrl}
        outputType={result?.outputType}
        fileTypeLabel={meta.label}
        fileTypeIcon={meta.icon}
        requiresAuth
        subtitle={`Your PDF has been converted to ${meta.label} successfully.`}
      />
    </>
  );
};

export default PdfToOfficeConverter;
